import React from "react";
import { useAuthorization } from "@common/providers";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { Route, Routes, Outlet } from "react-router";
import Show from "./Show";
import { useV2GetAccountQuery, useV2WhoAmIQuery } from "@generated";
import New from "./New";
import Edit from "./Edit";
import useListProjectVariables from "../../ProjectVariables/api/useListProjectVariables";

const ProfessionalServicesMspa = ({
  serviceType,
}: {
  serviceType: "professional_services" | "managed_services";
}): JSX.Element => {
  // Get Account. `/v2/account` serializes the account alone - it takes no
  // `include`, and nothing here reads related records anyway; consumers only
  // want `account-id`, `entitlements` and `field-labels` off `attributes`.
  const {
    data: account,
    error: accountError,
    isLoading: accountLoading,
    refetch: accountRefetch,
  } = useV2GetAccountQuery({});

  // Get permissions for current user
  const {
    data: currentUser,
    error: currentUserError,
    isLoading: currentUserLoading,
    refetch: currentUserRefetch,
  } = useV2WhoAmIQuery();

  const {
    allProjectVariables,
    isLoading: projectVariablesLoading,
    error,
    refetch,
    isFetching,
  } = useListProjectVariables("service");

  const authorized = useAuthorization();
  if (
    !authorized ||
    !account ||
    accountLoading ||
    !currentUser ||
    currentUserLoading ||
    !allProjectVariables ||
    projectVariablesLoading
  ) {
    return <ScopeStackSpinner />;
  }

  const permission = currentUser?.data?.attributes?.privileges;
  const professionalServicePermission = permission?.find(
    (privilege) => privilege.privilege === "settings.tasks"
  )?.["access-level"];
  const managedServicePermission = permission?.find(
    (privilege) => privilege.privilege === "settings.managed_services"
  )?.["access-level"];
  const auditLogPermission = permission?.find(
    (privilege) => privilege.privilege === "settings.audit_logs"
  )?.["access-level"];
  return (
    <Routes>
      <Route
        path="/"
        element={
          <Show
            account={account}
            servicePermission={
              serviceType === "professional_services"
                ? professionalServicePermission
                : managedServicePermission
            }
            serviceType={serviceType}
          />
        }
      />
      <Route path="/new" element={<Outlet />}>
        <Route
          index
          element={
            <New
              permission={
                serviceType === "professional_services"
                  ? professionalServicePermission
                  : managedServicePermission
              }
              allProjectVariables={allProjectVariables}
            />
          }
        />
      </Route>
      <Route path="/:id/edit" element={<Outlet />}>
        <Route
          index
          element={
            <Edit
              permission={
                serviceType === "professional_services"
                  ? professionalServicePermission
                  : managedServicePermission
              }
              auditLogPermission={auditLogPermission}
              account={account?.data}
              allProjectVariables={allProjectVariables}
            />
          }
        />
      </Route>
    </Routes>
  );
};

export default ProfessionalServicesMspa;
